/**
 * Solar elevation (degrees above the horizon) for an instant and a place, using
 * the NOAA solar position approximation. Accurate to well under a degree, which
 * is plenty for shading day, twilight and night on a 24-hour strip. Works at the
 * poles too: midnight sun and polar night simply never cross the horizon.
 */
const RAD = Math.PI / 180;

export function solarElevation(ms, lat, lng) {
  const jd = ms / 86400000 + 2440587.5;
  const t = (jd - 2451545) / 36525;
  const l0 = (280.46646 + t * (36000.76983 + t * 0.0003032)) % 360;
  const m = 357.52911 + t * (35999.05029 - 0.0001537 * t);
  const e = 0.016708634 - t * (0.000042037 + 0.0000001267 * t);
  const c =
    Math.sin(m * RAD) * (1.914602 - t * (0.004817 + 0.000014 * t)) +
    Math.sin(2 * m * RAD) * (0.019993 - 0.000101 * t) +
    Math.sin(3 * m * RAD) * 0.000289;
  const trueLong = l0 + c;
  const omega = 125.04 - 1934.136 * t;
  const lambda = trueLong - 0.00569 - 0.00478 * Math.sin(omega * RAD);
  const eps0 = 23 + (26 + (21.448 - t * (46.815 + t * (0.00059 - t * 0.001813))) / 60) / 60;
  const eps = eps0 + 0.00256 * Math.cos(omega * RAD);
  const decl = Math.asin(Math.sin(eps * RAD) * Math.sin(lambda * RAD));

  const y = Math.tan((eps / 2) * RAD) ** 2;
  const eqTime =
    4 / RAD *
    (y * Math.sin(2 * l0 * RAD) -
      2 * e * Math.sin(m * RAD) +
      4 * e * y * Math.sin(m * RAD) * Math.cos(2 * l0 * RAD) -
      0.5 * y * y * Math.sin(4 * l0 * RAD) -
      1.25 * e * e * Math.sin(2 * m * RAD));

  const minutesUtc = ((ms % 86400000) + 86400000) % 86400000 / 60000;
  const trueSolarTime = (minutesUtc + eqTime + 4 * lng + 1440) % 1440;
  const hourAngle = trueSolarTime / 4 < 0 ? trueSolarTime / 4 + 180 : trueSolarTime / 4 - 180;
  const cosZenith =
    Math.sin(lat * RAD) * Math.sin(decl) + Math.cos(lat * RAD) * Math.cos(decl) * Math.cos(hourAngle * RAD);
  return 90 - Math.acos(Math.max(-1, Math.min(1, cosZenith))) / RAD;
}

/** Light phase: 'day' | 'twilight' | 'night' (civil twilight down to -6°). */
export function phase(elevation) {
  if (elevation > -0.833) return 'day';
  if (elevation > -6) return 'twilight';
  return 'night';
}

/** First sunrise and sunset (instants) inside [from, to), sampled per minute-ish. */
export function sunEvents(lat, lng, from, to) {
  const step = 5 * 60e3;
  let prev = solarElevation(from, lat, lng) > -0.833;
  let sunrise = null;
  let sunset = null;
  for (let t = from + step; t < to; t += step) {
    const up = solarElevation(t, lat, lng) > -0.833;
    if (up && !prev && sunrise === null) sunrise = t;
    if (!up && prev && sunset === null) sunset = t;
    prev = up;
  }
  return { sunrise, sunset };
}
